import axios, { AxiosError } from 'axios';
import {
  incrementGlobalLoading,
  decrementGlobalLoading,
} from '@/components/loading';
import { showGlobalError } from '@/components/feedback';
import type { ApiResult, ApiError } from '@/types/api';

export interface ApiResponse<T = unknown> {
  data?: T;
  error?: {
    message: string;
    status?: number;
  };
}

interface ErrorResponse {
  statusCode?: number;
  message?: string;
  error?: string | { message: string };
  errors?: { [key: string]: string[] } | Array<{ message: string } | string>;
  details?: string;
}

type HttpMethod = 'get' | 'post' | 'put' | 'patch' | 'delete';
type RequestConfig<TRequest, TParams> = {
  url: string;
  method: HttpMethod;
  payload?: TRequest;
  params?: TParams;
  headers?: Record<string, string>;
  showError?: boolean;
  showLoading?: boolean;
  customErrorMessage?: string;
};

// Error handling helpers
const getValidationErrors = (data: ErrorResponse): string => {
  if (data?.errors && !Array.isArray(data.errors)) {
    return Object.entries(data.errors)
      .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
      .join('; ');
  }
  return '';
};

const getErrorMessage = (error: AxiosError<ErrorResponse>) => {
  const { response } = error;
  if (!response) {
    return 'Network error: Please check your connection';
  }

  const data = response.data;
  let message = '';

  if (response.status === 400) {
    message = getValidationErrors(data);
    if (message) return message;
  }

  message =
    typeof data === 'string'
      ? data
      : data?.message
        ? data.message
        : typeof data?.error === 'object'
          ? data.error.message
          : data?.error
            ? String(data.error)
            : data?.details
              ? data.details
              : 'An error occurred';

  if (import.meta.env.DEV && response.status !== 500) {
    message = `[${response.status}] ${message}`;
  }

  return message || response.statusText;
};

const api = axios.create({
  baseURL: '/',
  timeout: 300000, // 5 minutes for file uploads
  withCredentials: true, // Enable cookies for all requests
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for loading state
api.interceptors.request.use(
  config => {
    // Only show loading for requests that don't opt out
    if (config.showLoading !== false) {
      incrementGlobalLoading();
    }
    return config;
  },
  error => {
    decrementGlobalLoading();
    return Promise.reject(error);
  }
);

// Response interceptor for centralized 401 handling and loading state
api.interceptors.response.use(
  response => {
    // Decrement loading counter on successful response
    decrementGlobalLoading();
    return response;
  },
  async error => {
    // Always decrement loading counter on error
    decrementGlobalLoading();

    if (error.response?.status === 401) {
      const { useAuthStore } = await import('@/features/auth/store');
      const { router } = await import('@/router');

      useAuthStore.setState({
        user: null,
        isAuthenticated: false,
        error: null,
      });
      await router.navigate({
        to: '/auth/login',
        search: { redirectTo: router.state.location.pathname },
      });
    } else {
      // Show error message for non-401 errors (optionally gated by showError flag)
      const config = error.config;
      if (config?.showError !== false) {
        const errorMessage = getErrorMessage(error);
        showGlobalError(errorMessage);
      }
    }

    return Promise.reject(error);
  }
);

// Base request handler
const makeRequest = async <TRequest, TResponse, TParams = undefined>(
  config: RequestConfig<TRequest, TParams>
): Promise<ApiResult<TResponse>> => {
  try {
    const response = await api.request<TResponse>({
      url: config.url,
      method: config.method,
      data: config.payload,
      params: config.params,
      headers: config.headers,
      showError: config.showError,
      showLoading: config.showLoading,
    });

    return { isSuccess: true, result: response.data };
  } catch (error) {
    const errorMessage =
      config.customErrorMessage ||
      (error instanceof AxiosError
        ? getErrorMessage(error)
        : error instanceof Error
          ? error.message
          : 'An unknown error occurred');

    const apiError: ApiError = {
      message: errorMessage,
      status: error instanceof AxiosError ? error.response?.status : undefined,
      code: error instanceof AxiosError ? error.code : undefined,
    };

    return { isSuccess: false, error: apiError };
  }
};

type RequestOptions = {
  headers?: Record<string, string>;
  showError?: boolean;
  showLoading?: boolean;
  customErrorMessage?: string;
};

// Modern apiClient interface
export const apiClient = {
  post: <TRequest, TResponse>(
    url: string,
    payload: TRequest,
    config?: RequestOptions
  ) =>
    makeRequest<TRequest, TResponse>({
      url,
      method: 'post',
      payload,
      headers: config?.headers,
      showError: config?.showError,
      showLoading: config?.showLoading,
      customErrorMessage: config?.customErrorMessage,
    }),

  get: <TResponse, TParams>(
    url: string,
    params?: TParams,
    config?: RequestOptions
  ) =>
    makeRequest<never, TResponse, TParams>({
      url,
      method: 'get',
      params,
      headers: config?.headers,
      showError: config?.showError,
      showLoading: config?.showLoading,
      customErrorMessage: config?.customErrorMessage,
    }),

  deleteBody: <TRequest, TResponse>(
    url: string,
    payload: TRequest,
    config?: RequestOptions
  ) =>
    makeRequest<TRequest, TResponse>({
      url,
      method: 'delete',
      payload,
      headers: config?.headers,
      showError: config?.showError,
      showLoading: config?.showLoading,
      customErrorMessage: config?.customErrorMessage,
    }),

  delete: <TResponse, TParams>(
    url: string,
    params?: TParams,
    config?: RequestOptions
  ) =>
    makeRequest<never, TResponse, TParams>({
      url,
      method: 'delete',
      params,
      headers: config?.headers,
      showError: config?.showError,
      showLoading: config?.showLoading,
      customErrorMessage: config?.customErrorMessage,
    }),

  put: <TRequest, TResponse>(
    url: string,
    payload: TRequest,
    config?: RequestOptions
  ) =>
    makeRequest<TRequest, TResponse>({
      url,
      method: 'put',
      payload,
      headers: config?.headers,
      showError: config?.showError,
      showLoading: config?.showLoading,
      customErrorMessage: config?.customErrorMessage,
    }),

  patch: <TRequest, TResponse>(
    url: string,
    payload: TRequest,
    config?: RequestOptions
  ) =>
    makeRequest<TRequest, TResponse>({
      url,
      method: 'patch',
      payload,
      headers: config?.headers,
      showError: config?.showError,
      showLoading: config?.showLoading,
      customErrorMessage: config?.customErrorMessage,
    }),
};
